/**
 * Server-side search/filter for HFDataTable: debounce, abort, and race-safe fetch.
 */
import { useEffect, useRef, useState } from 'react';
import type { GridValidRowModel } from '@mui/x-data-grid';

export type HFServerSearchRequest = {
  /** Global quick-filter text, words joined by a space. `''` when empty. */
  query: string;
  /** Per-column header queries, keyed by field. Empty object when none. */
  columns: Record<string, string>;
  /** Aborted when a newer request supersedes this one, or on unmount. */
  signal: AbortSignal;
};

/**
 * Run global + column search on the server. Omit for client-side search.
 * Summary aggregates only the rows the server returns.
 */
export type HFServerSearch<R extends GridValidRowModel = GridValidRowModel> = {
  fetch: (request: HFServerSearchRequest) => Promise<readonly R[]>;
  /** Default 300. */
  debounceMs?: number;
  onError?: (error: unknown) => void;
};

function isAbortError(error: unknown): boolean {
  return (
    (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

export type UseServerSearchArgs<R extends GridValidRowModel> = {
  serverSearch: HFServerSearch<R> | undefined;
  query: string;
  columnQueries: Record<string, string>;
  /** When true, defer applying a successful response until cleared (e.g. row drag). */
  paused?: boolean;
};

/**
 * Debounced server fetch keyed by quick-filter + column queries and by `fetch` itself, so callers must memoize `fetch`.
 * Skips the initial empty request so parent-provided `rows` stay the first paint.
 */
export function useServerSearch<R extends GridValidRowModel>({
  serverSearch,
  query,
  columnQueries,
  paused = false,
}: UseServerSearchArgs<R>): { rows: R[] | null; loading: boolean } {
  const [rows, setRows] = useState<R[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [pendingRows, setPendingRows] = useState<R[] | null>(null);

  const fetchRef = useRef(serverSearch?.fetch);
  const onErrorRef = useRef(serverSearch?.onError);
  const pausedRef = useRef(paused);
  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const skippedInitialEmpty = useRef(false);

  useEffect(() => {
    fetchRef.current = serverSearch?.fetch;
    onErrorRef.current = serverSearch?.onError;
  }, [serverSearch?.fetch, serverSearch?.onError]);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  const enabled = Boolean(serverSearch);
  const fetchKey = serverSearch?.fetch;
  const debounceMs = serverSearch?.debounceMs ?? 300;
  const requestKey = JSON.stringify({ query, columns: columnQueries });

  // Drop cached server rows when switching back to client mode.
  if (!enabled && rows !== null) {
    setRows(null);
  }
  if (!enabled && loading) {
    setLoading(false);
  }
  if (!enabled && pendingRows !== null) {
    setPendingRows(null);
  }

  // Flush a response that landed mid-drag once dragging ends.
  if (!paused && pendingRows != null) {
    setRows(pendingRows);
    setPendingRows(null);
  }

  useEffect(() => {
    if (!enabled) {
      skippedInitialEmpty.current = false;
      return undefined;
    }

    const isEmpty = query === '' && Object.keys(columnQueries).length === 0;
    if (!skippedInitialEmpty.current && isEmpty) {
      skippedInitialEmpty.current = true;
      return undefined;
    }
    skippedInitialEmpty.current = true;

    const timer = window.setTimeout(() => {
      const id = ++requestIdRef.current;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setPendingRows(null);

      const fetchFn = fetchRef.current;
      if (!fetchFn) return;

      setLoading(true);
      void fetchFn({
        query,
        columns: { ...columnQueries },
        signal: controller.signal,
      })
        .then((result) => {
          if (id !== requestIdRef.current) return;
          const next = [...result] as R[];
          if (pausedRef.current) {
            setPendingRows(next);
          } else {
            setRows(next);
          }
        })
        .catch((error: unknown) => {
          if (id !== requestIdRef.current) return;
          if (isAbortError(error)) return;
          onErrorRef.current?.(error);
        })
        .finally(() => {
          if (id === requestIdRef.current) setLoading(false);
        });
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [enabled, requestKey, query, columnQueries, debounceMs, fetchKey]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      requestIdRef.current += 1;
    };
  }, []);

  return { rows: enabled ? rows : null, loading: enabled ? loading : false };
}
